import React from "react";

export type RadioOption = {
  label: string;
  value: string;
};

type RadioGroupProps = {
  options: RadioOption[];
  disabled?: boolean;
  vertical?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>;

/**
 * A row (or column) of radio buttons with consistent enabled / disabled styling.
 * Pass `...register("fieldName")` as spread props.
 * Optionally pass `value` to control which option is checked (overrides native state).
 * Use `vertical` for questions with long option labels.
 */
export const RadioGroup = React.forwardRef<HTMLInputElement, RadioGroupProps>(
  function RadioGroup(
    { options, disabled, value, vertical, ...inputProps },
    ref
  ) {
    return (
      <div
        className={vertical ? "flex flex-col gap-2" : "flex items-center gap-6"}
      >
        {options.map((opt, i) => (
          <label
            key={opt.value}
            className={[
              "flex items-center gap-2 text-sm select-none",
              disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer",
            ].join(" ")}
          >
            <input
              {...inputProps}
              ref={ref}
              type="radio"
              value={opt.value}
              disabled={disabled}
              {...(value !== undefined ? { checked: opt.value === value } : {})}
              className={[
                "accent-blue-600 w-3.5 h-3.5",
                disabled ? "cursor-not-allowed" : "cursor-pointer",
              ].join(" ")}
            />
            <span
              className={disabled ? "line-through decoration-gray-400" : ""}
            >
              {opt.label}
            </span>
          </label>
        ))}
      </div>
    );
  }
);
